import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaFilter, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Loader/Loader';
import './ExamHistory.css';

const ExamHistory = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exams, setExams] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');

  const fetchHistory = useCallback(async () => {
    try {
      let url = `${api.getExamHistory}?page=${page}&limit=10`;
      if (filterStatus) url += `&status=${filterStatus}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        setExams(res.data.data.exams || []);
        setTotalPages(res.data.data.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load exam history');
    } finally {
      setLoading(false);
    }
  }, [token, page, filterStatus]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (loading) return <Loader message="Loading exam history..." />;

  return (
    <div className="exam-history-page">
      <header className="eh-header">
        <button className="eh-back-btn" onClick={() => navigate('/university/dashboard')}>
          <FaArrowLeft /> Back
        </button>
        <h1>Exam History</h1>
        <div className="eh-filter">
          <FaFilter />
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="evaluated">Evaluated</option>
            <option value="submitted">Submitted</option>
            <option value="in-progress">In Progress</option>
          </select>
        </div>
      </header>

      {exams.length === 0 ? (
        <div className="eh-empty">
          <h2>No exams found</h2>
          <p>Take your first exam to see your history here.</p>
          <button onClick={() => navigate('/university/exam')}>Take an Exam</button>
        </div>
      ) : (
        <>
          <div className="eh-list">
            {exams.map((exam) => (
              <div
                key={exam._id}
                className="eh-card"
                onClick={() => {
                  if (exam.status === 'evaluated') navigate(`/university/result/${exam._id}`);
                  else if (exam.status === 'in-progress' || exam.status === 'created') navigate(`/university/exam/${exam._id}`);
                }}
              >
                <div className="eh-card-left">
                  <h3>{exam.subject}</h3>
                  <span className="eh-meta">{exam.category} • {exam.subcategory}</span>
                  <div className="eh-badges">
                    <span className={`eh-diff eh-diff-${exam.level}`}>{exam.level}</span>
                    <span className={`eh-status eh-status-${exam.status}`}>{exam.status}</span>
                  </div>
                </div>
                <div className="eh-card-right">
                  {exam.status === 'evaluated' && (
                    <span className={`eh-score ${exam.percentage >= 45 ? 'pass' : 'fail'}`}>
                      {exam.percentage?.toFixed(0)}%
                    </span>
                  )}
                  <span className="eh-date">{new Date(exam.createdAt).toLocaleDateString()}</span>
                  <FaChevronRight className="eh-chevron" />
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="eh-pagination">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExamHistory;
